import React, { useState, useEffect, useCallback } from 'react';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { useModerator } from '../hooks/useModerator';
import { useNavigate, Link } from 'react-router-dom';
import { ProtectedRoute } from '../auth/ProtectedRoute';
import { Button } from '../components/common/Button';
import { Modal } from '../components/common/Modal';
import { Textarea } from '../components/common/Textarea';
import { moderationApi } from '../api/moderation';
import type { Post } from '../types/post';

const PAGE_SIZE = 50;

const FlaggedPostsPageContent: React.FC = () => {
  const isModerator = useModerator();
  const navigate = useNavigate();
  const [page] = useState(0);
  const [flagged, setFlagged] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approvePostId, setApprovePostId] = useState<string | null>(null);
  const [rejectPostId, setRejectPostId] = useState<string | null>(null);
  const [approveReason, setApproveReason] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [rejectError, setRejectError] = useState<string | null>(null);

  const fetchFlagged = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await moderationApi.getFlaggedPosts(page, PAGE_SIZE);
      setFlagged(res.content);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch flagged posts');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    if (!isModerator) {
      navigate('/');
      return;
    }
    fetchFlagged();
  }, [fetchFlagged, isModerator, navigate]);

  const handleApprove = (postId: string) => {
    setApprovePostId(postId);
    setApproveReason('');
    setApproveError(null);
  };

  const confirmApprove = async () => {
    if (!approvePostId) return;
    setApproving(true);
    setApproveError(null);
    try {
      await moderationApi.approveFlag(approvePostId, { reason: approveReason.trim() || undefined });
      setApprovePostId(null);
      setApproveReason('');
      await fetchFlagged();
    } catch (err) {
      setApproveError(err instanceof Error ? err.message : 'Failed to approve flag');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = (postId: string) => {
    setRejectPostId(postId);
    setRejectReason('');
    setRejectError(null);
  };

  const confirmReject = async () => {
    if (!rejectPostId) return;
    setRejecting(true);
    setRejectError(null);
    try {
      await moderationApi.rejectFlag(rejectPostId, { reason: rejectReason.trim() || undefined });
      setRejectPostId(null);
      setRejectReason('');
      await fetchFlagged();
    } catch (err) {
      setRejectError(err instanceof Error ? err.message : 'Failed to reject flag');
    } finally {
      setRejecting(false);
    }
  };

  const truncateText = (text: string, maxLength: number = 150): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  if (!isModerator) return null;

  if (loading && flagged.length === 0) {
    return <LoadingSpinner size="large" />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  return (
    <div className="deleted-posts-page">
      <div className="deleted-posts-page-header">
        <h1>Flagged Posts</h1>
        <p className="deleted-posts-description">
          Posts reported as inappropriate. Approve (uphold) or reject (dismiss) each flag. Only visible to moderators.
        </p>
      </div>

      {flagged.length === 0 ? (
        <div className="deleted-posts-empty">
          <p>No flagged posts.</p>
        </div>
      ) : (
        <div className="deleted-posts-table-container">
          <table className="deleted-posts-table">
            <thead>
              <tr>
                <th>Date flagged</th>
                <th>Reason</th>
                <th>Post title & content</th>
                <th>Author</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {flagged.map((post) => (
                <tr key={post.id}>
                  <td className="deletion-reason-cell" data-label="Date flagged">{formatDate(post.flaggedAt)}</td>
                  <td className="deletion-reason-cell" data-label="Reason">
                    {post.flagReason || <em>No reason provided</em>}
                  </td>
                  <td className="post-content-cell" data-label="Post">
                    <Link to={`/posts/${post.id}?includeDeleted=true`} className="post-link">
                      {post.title && (
                        <div className="post-title-truncated">
                          <strong>{truncateText(post.title, 80)}</strong>
                        </div>
                      )}
                      <div className="post-content-truncated">
                        {truncateText(post.content, 150)}
                      </div>
                    </Link>
                  </td>
                  <td data-label="Author">{post.authorUsername ?? post.authorId}</td>
                  <td className="actions-cell" data-label="Actions">
                    <div className="deleted-posts-actions">
                      <Button variant="success" size="small" onClick={() => handleApprove(post.id)}>
                        Approve
                      </Button>
                      <Button variant="danger" size="small" onClick={() => handleReject(post.id)}>
                        Reject
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={approvePostId !== null}
        onClose={() => {
          setApprovePostId(null);
          setApproveReason('');
          setApproveError(null);
        }}
        title="Approve flag"
      >
        <div className="restore-modal">
          <p>Uphold the flag? The post will remain hidden.</p>
          <Textarea
            label="Reason (optional)"
            value={approveReason}
            onChange={(e) => setApproveReason(e.target.value)}
            placeholder="Reason for approval..."
            rows={4}
          />
          {approveError && <div className="error-message">{approveError}</div>}
          <div className="modal-actions">
            <Button variant="primary" onClick={confirmApprove} disabled={approving}>
              {approving ? 'Approving…' : 'Approve'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setApprovePostId(null);
                setApproveReason('');
                setApproveError(null);
              }}
              disabled={approving}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={rejectPostId !== null}
        onClose={() => {
          setRejectPostId(null);
          setRejectReason('');
          setRejectError(null);
        }}
        title="Reject flag"
      >
        <div className="restore-modal">
          <p>Dismiss the flag? The post will become visible again.</p>
          <Textarea
            label="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason for rejection..."
            rows={4}
          />
          {rejectError && <div className="error-message">{rejectError}</div>}
          <div className="modal-actions">
            <Button variant="primary" onClick={confirmReject} disabled={rejecting}>
              {rejecting ? 'Rejecting…' : 'Reject'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setRejectPostId(null);
                setRejectReason('');
                setRejectError(null);
              }}
              disabled={rejecting}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export const FlaggedPostsPage: React.FC = () => {
  return (
    <ProtectedRoute>
      <FlaggedPostsPageContent />
    </ProtectedRoute>
  );
};
